import type { TagOption } from 'factories/tags';
import { flatten, uniq } from 'lodash-es';
import type { TagsDictionary } from 'redux/modules/tags.types';
import type { Tag } from 'types/tag';
import { keyBy } from './typesafe';

export function applyTagGroupFilter(
  tags: Array<Tag>,
  filterGroup: 'active' | 'archived' | null
): Array<Tag> {
  if (filterGroup == null) {
    return tags;
  }
  return tags.reduce((prev: Array<Tag>, tag: Tag) => {
    if (tag.active === true && filterGroup === 'active') {
      const filteredChildren = (tag.children || []).filter(p => p.active === true);
      return [
        ...prev,
        {
          ...tag,
          children: filteredChildren,
        },
      ];
    } else if (filterGroup === 'archived') {
      if (tag.active === false) {
        const filteredChildren = (tag.children || []).filter(p => p.active === false);
        return [
          ...prev,
          {
            ...tag,
            children: filteredChildren,
          },
        ];
      }
      const filteredChildren = (tag.children || []).filter(p => p.active === false);

      if (filteredChildren.length > 0) {
        return [
          ...prev,
          {
            ...tag,
            children: filteredChildren,
          },
        ];
      }
    }
    return prev;
  }, []);
}

export function processTagSelection(
  tag: TagOption,
  {
    options,
    selection,
    singleChildSelect,
  }: {
    options: Array<TagOption>;
    selection: Array<number>;
    singleChildSelect?: boolean;
  }
): Array<number> {
  const selectionSet = new Set(selection);
  const tagOptionById = {
    ...keyBy(options, tag => tag.id),
    ...keyBy(flatten(options.map(tag => tag.children ?? [])), tag => tag.id),
  };

  const isSelected = selectionSet.has(tag.id);

  const childrenIds = tag.children?.map(child => child.id) ?? [];
  const hasChildren = childrenIds.length > 0;
  const hasSelectedChildren = childrenIds.some(childId => selectionSet.has(childId));
  const isParentSelected = tag.parent_id && selectionSet.has(tag.parent_id);

  if (isSelected) {
    return selection.filter(id => id !== tag.id);
  }
  if (tag.parent_id != null) {
    const siblings =
      (tag.parent_id &&
        tagOptionById[tag.parent_id].children?.filter(child => child.id !== tag.id)) ||
      [];
    const siblingIds = siblings.map(sibling => sibling.id);
    const allSiblingsSelected =
      siblingIds.length > 0 && siblingIds.every(id => selectionSet.has(id));

    if (isParentSelected) {
      return selection.filter(id => id !== tag.parent_id).concat(siblingIds);
    }
    if (!isSelected) {
      if (singleChildSelect) {
        return selection.filter(id => !siblingIds.includes(id)).concat(tag.id);
      }
      if (allSiblingsSelected) {
        return selection.filter(id => !siblingIds.includes(id)).concat(tag.parent_id);
      }
      return selection.concat(tag.id);
    }
  } else {
    if (!hasChildren) {
      return selection.concat(tag.id);
    }
    // It's a parent tag
    if (hasSelectedChildren) {
      if (singleChildSelect) {
        return selection.filter(id => !childrenIds.includes(id));
      }
      return selection.filter(id => !childrenIds.includes(id)).concat(tag.id);
    } else {
      if (singleChildSelect) {
        return selection.concat(childrenIds[0]);
      }
      return selection.concat(tag.id);
    }
  }

  return selection;
}

export function attachParentIdToChildTagsIds(ids: Array<number>, entities: TagsDictionary) {
  const tagIds = ids.reduce((allIds: Array<number>, id: number) => {
    const tag = entities[id];
    if (tag && tag.parent_id) {
      return allIds.concat([id, tag.parent_id]);
    }
    return allIds.concat(id);
  }, []);

  return uniq(tagIds);
}
